import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { systemPrompts } from '@/api/systemPrompts';
import type { SystemPromptRow } from '@/api/systemPrompts';
import { PromptDetailPanel } from './PromptDetailPanel';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronDown, ChevronRight, Search } from 'lucide-react';

// Group by category
function groupByCategory(rows: SystemPromptRow[]): Record<string, SystemPromptRow[]> {
  const groups: Record<string, SystemPromptRow[]> = {};
  for (const row of rows) {
    const cat = row.category ?? 'general';
    if (!groups[cat]) groups[cat] = [];
    groups[cat]!.push(row);
  }
  return groups;
}

interface PromptRowProps {
  prompt: SystemPromptRow;
}

function PromptRow({ prompt }: PromptRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-slate-400">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span className="font-mono text-sm text-slate-700 flex-1">{prompt.prompt_key}</span>
        <Badge variant="outline" className="text-[10px] font-normal shrink-0">
          {prompt.category}
        </Badge>
      </button>
      {expanded && <PromptDetailPanel prompt={prompt} />}
    </div>
  );
}

interface CategoryGroupProps {
  category: string;
  prompts: SystemPromptRow[];
}

function CategoryGroup({ category, prompts }: CategoryGroupProps) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden mb-4">
      <div className="bg-slate-100 px-4 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          {category}
        </p>
      </div>
      <div>
        {prompts.map((p) => (
          <PromptRow key={p.id} prompt={p} />
        ))}
      </div>
    </div>
  );
}

export function SystemPromptsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['systemPrompts'],
    queryFn: () => systemPrompts.list(),
  });

  const rows = data?.rows ?? [];
  const filtered = search.trim()
    ? rows.filter((r) => {
        // Case-insensitive search across key, category, and description.
        // Prior code only lowercased the query — if prompt_key contained
        // any uppercase it would never match.
        const q = search.toLowerCase();
        return (
          r.prompt_key.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q) ||
          (r.description?.toLowerCase().includes(q) ?? false)
        );
      })
    : rows;

  const grouped = groupByCategory(filtered);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('systemPrompts.title')}
        description={t('systemPrompts.description')}
      />

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('systemPrompts.searchPlaceholder')}
          className="pl-8 h-9"
        />
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('systemPrompts.loadingPrompts')}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600">
          {t('systemPrompts.failedToLoad')}
        </p>
      )}

      {!isLoading && filtered.length === 0 && (
        <p className="text-sm text-slate-500">{t('systemPrompts.noPromptsFound')}</p>
      )}

      {Object.entries(grouped).map(([cat, prompts]) => (
        <CategoryGroup key={cat} category={cat} prompts={prompts} />
      ))}
    </div>
  );
}
